/**
 * DropZoneOverlay Component
 * Simple drop zone overlay that appears when dragging files over the message area.
 * Media files (image/video) default to inline preview, others to file download.
 *
 * Always mounted in the DOM — visibility controlled by parent via `visible` prop.
 */

import { isCompressibleMedia } from '@/utils/media-metadata.util';

interface DropZoneOverlayProps {
  visible: boolean;
  onDrop: (files: File[], sendOption: 'image' | 'file') => void;
}

const DropZoneOverlay = ({ visible, onDrop }: DropZoneOverlayProps) => {
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Media files → inline preview, everything else → file download
    const hasMedia = files.some((f) => isCompressibleMedia(f));
    onDrop(files, hasMedia ? 'image' : 'file');
  };

  return (
    <div
      className={`absolute inset-0 z-50 flex items-center justify-center transition-opacity duration-150 ${
        visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      <div className="absolute inset-0 bg-primary-500/10 dark:bg-primary-500/20 backdrop-blur-sm" />
      <div className="relative text-center pointer-events-none select-none">
        <svg
          className="mx-auto h-16 w-16 text-primary-500 dark:text-primary-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
          />
        </svg>
        <p className="mt-4 text-lg font-semibold text-primary-600 dark:text-primary-300">
          Пуснете файловете тук
        </p>
        <p className="mt-1 text-sm text-primary-500/70 dark:text-primary-400/60">
          Изображения и видео се показват директно в чата
        </p>
      </div>
    </div>
  );
};

export default DropZoneOverlay;
